/**
 * Вкладка «Конспект» AI Lab: разговоры бота (журнал) и живые чаты за 14 дней — лента с метками конспекта и
 * блок, как его получит модель; ниже — «Неузнанные вопросы» (наши вопросы без темы, очередь в словарь).
 * `?tab=konspekt&src=bot|chats&chat=<id>|room=<tg-…>`. Спека `docs/specs/2026-11-05-dialog-konspekt-design.md` §7.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";

import { KonspektBox } from "@/components/KonspektBox";
import { KonspektChips } from "@/components/KonspektChips";
import { ApiFailure } from "@/lib/api";
import {
  labCallQuery,
  labCallsQuery,
  labKonspektChatQuery,
  labKonspektChatsQuery,
  labKonspektUnknownQuery,
  type LabKonspekt,
} from "@/lib/voiceLab";

const control = "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg";
const seg = (on: boolean) =>
  `rounded-[var(--radius-ui)] border px-2.5 py-1 text-[12.5px] ${on ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"}`;

/** «2026-11-05 12:00:00» / ISO → «05.11 12:00». */
const когда = (s: string) => `${s.slice(8, 10)}.${s.slice(5, 7)} ${s.slice(11, 16)}`;

type Реплика = { i: number; who: "user" | "agent"; text: string; auto?: boolean };

/** Лента реплик с метками конспекта под каждой. */
function Дорожка({ реплики, konspekt }: { реплики: Реплика[]; konspekt: LabKonspekt }) {
  const без_темы = new Set(konspekt.unknown.map((u) => u.turn));
  return (
    <section className="flex flex-col gap-2" data-annot="лента с метками конспекта">
      {реплики.map((m) => {
        const наша = m.who === "agent";
        return (
          <div key={m.i} className={`flex flex-col gap-1 ${наша ? "items-start" : "items-end"}`}>
            <div
              className={`max-w-[85%] whitespace-pre-wrap break-words rounded-[var(--radius-ui)] border px-2.5 py-1.5 text-[13px] ${наша ? "border-border bg-bg-subtle" : "border-accent/40 bg-accent/5"}`}
            >
              <span className="mr-1.5 text-[11.5px] text-fg-muted">
                {наша ? (m.auto ? "Робот" : "Мы") : "Клиентка"}
              </span>
              {m.text}
            </div>
            <KonspektChips turn={konspekt.turns[String(m.i)]} className={наша ? "" : "justify-end"} />
            {наша && без_темы.has(m.i) && (
              <span className="text-[11.5px] text-warn">есть вопрос без темы в словаре</span>
            )}
          </div>
        );
      })}
    </section>
  );
}

function ЧатПросмотр({ id }: { id: number }) {
  const q = useQuery(labKonspektChatQuery(id));
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Загрузка…</div>;
  if (q.error)
    return (
      <div className="text-[12.5px] text-err">
        {q.error instanceof ApiFailure && q.error.status === 404 ? "чат не найден" : "Не загрузилось"}
      </div>
    );
  return (
    <>
      <div className="text-[12.5px] text-fg-muted">Чат {id}</div>
      <KonspektBox konspekt={q.data} showEmpty />
      <Дорожка реплики={q.data.messages} konspekt={q.data} />
    </>
  );
}

function БотПросмотр({ room }: { room: string }) {
  const q = useQuery(labCallQuery(room));
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Загрузка…</div>;
  if (q.error) return <div className="text-[12.5px] text-err">Разговор не найден</div>;
  const k = q.data.konspekt;
  if (!k) return <div className="text-[12.5px] text-fg-muted">У этого разговора конспекта нет</div>;
  const реплики = q.data.turns.map((t, i) => ({ i, who: t.who === "agent" ? ("agent" as const) : ("user" as const), text: t.text }));
  return (
    <>
      <div className="text-[12.5px] text-fg-muted">
        {q.data.row.name ?? "—"} · {когда(q.data.row.started)} · {room}
      </div>
      <KonspektBox konspekt={k} showEmpty />
      <Дорожка реплики={реплики.filter((r) => r.text)} konspekt={k} />
    </>
  );
}

function Неузнанные({ open }: { open: (e: { chat?: number; room?: string }) => void }) {
  const q = useQuery(labKonspektUnknownQuery(14));
  return (
    <section className="flex max-w-3xl flex-col gap-1.5" data-annot="неузнанные вопросы">
      <h2 className="text-[13.5px] font-semibold">Неузнанные вопросы</h2>
      <div className="text-[12px] text-fg-muted">
        Наши предложения с «?» без темы в словаре — кандидаты в словарь. По последним чатам с вопросами за 14 дней
        (до 300) и журналу бота.
      </div>
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Загрузка…</div>
      ) : q.error ? (
        <div className="text-[12.5px] text-err">Не загрузилось</div>
      ) : !q.data.length ? (
        <div className="text-[12.5px] text-fg-muted">Неузнанных вопросов нет</div>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-[var(--radius-ui)] border border-border text-[12.5px]">
          {q.data.map((u) => (
            <li key={u.sentence} className="flex items-baseline gap-2 px-2.5 py-1.5">
              <span className="min-w-0 flex-1 break-words">{u.sentence}</span>
              <span className="tabular-nums text-fg-muted" title="сколько раз спросили">
                ×{u.count}
              </span>
              <button
                type="button"
                className="shrink-0 text-accent hover:underline"
                onClick={() =>
                  open(u.example.chat_id != null ? { chat: u.example.chat_id } : { room: u.example.room! })
                }
              >
                пример
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function VoiceLabKonspekt() {
  const [params, setParams] = useSearchParams();
  const chat = Number(params.get("chat")) || null;
  const room = params.get("room");
  const src = params.get("src") === "chats" || (chat && !room) ? "chats" : params.get("src") === "bot" || room ? "bot" : "chats";
  const [поиск, setПоиск] = useState("");
  const chats = useQuery({ ...labKonspektChatsQuery(14, 50), enabled: src === "chats" });
  const calls = useQuery({ ...labCallsQuery(), enabled: src === "bot" });
  const go = (next: { src?: string; chat?: number | null; room?: string | null }) => {
    const n: Record<string, string> = { tab: "konspekt", src: next.src ?? src };
    if (next.chat) n.chat = String(next.chat);
    if (next.room) n.room = next.room;
    setParams(n);
  };
  const строки = (calls.data?.calls ?? []).filter((r) => r.channel === "chat" || r.turn === "text");
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]" data-annot="источник конспекта">
        <button type="button" className={seg(src === "bot")} onClick={() => go({ src: "bot" })}>
          Бот
        </button>
        <button type="button" className={seg(src === "chats")} onClick={() => go({ src: "chats" })}>
          Чаты
        </button>
        <form
          className="flex items-center gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number(поиск.trim());
            if (n > 0) go({ src: "chats", chat: n });
          }}
        >
          <input
            className={`${control} w-36`}
            inputMode="numeric"
            placeholder="номер чата"
            value={поиск}
            onChange={(e) => setПоиск(e.target.value)}
          />
          <button type="submit" className={seg(false)}>
            Открыть
          </button>
        </form>
      </div>
      <div className="grid items-start gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="flex max-h-[70vh] flex-col overflow-y-auto rounded-[var(--radius-ui)] border border-border text-[12.5px]">
          {src === "chats" ? (
            chats.isPending ? (
              <div className="p-2.5 text-fg-muted">Загрузка…</div>
            ) : chats.error ? (
              <div className="p-2.5 text-err">Не загрузилось</div>
            ) : !chats.data.length ? (
              <div className="p-2.5 text-fg-muted">Чатов, где мы спрашивали, за 14 дней нет</div>
            ) : (
              <>
                <div className="flex items-baseline border-b border-border bg-bg-subtle px-2.5 py-1 text-[11.5px] text-fg-muted">
                  <span className="flex-1">чат · последнее сообщение</span>
                  <span className="mr-3">спросили</span>
                  <span title="спросили 2+ раза, ответа нет">закрыто вопросов</span>
                </div>
                {chats.data.map((c) => (
                  <button
                    key={c.chat_id}
                    type="button"
                    onClick={() => go({ src: "chats", chat: c.chat_id })}
                    className={`flex items-baseline gap-2 px-2.5 py-1.5 text-left hover:bg-bg-subtle ${chat === c.chat_id ? "bg-accent/10" : ""}`}
                  >
                    <span className="flex-1">
                      №{c.chat_id} <span className="text-fg-muted">· {когда(c.last_at)}</span>
                    </span>
                    <span className="mr-8 tabular-nums">{c.asked}</span>
                    <span className="w-6 text-right tabular-nums" title="спросили 2+ раза, ответа нет">
                      {c.closed}
                    </span>
                  </button>
                ))}
              </>
            )
          ) : calls.isPending ? (
            <div className="p-2.5 text-fg-muted">Загрузка…</div>
          ) : calls.error ? (
            <div className="p-2.5 text-err">Не загрузилось</div>
          ) : !строки.length ? (
            <div className="p-2.5 text-fg-muted">Разговоров бота в журнале нет</div>
          ) : (
            строки.map((r) => (
              <button
                key={r.room}
                type="button"
                onClick={() => go({ src: "bot", room: r.room })}
                className={`flex flex-col px-2.5 py-1.5 text-left hover:bg-bg-subtle ${room === r.room ? "bg-accent/10" : ""}`}
              >
                <span>
                  {r.name ?? "—"} <span className="text-fg-muted">· {когда(r.started)}</span>
                </span>
                <span className="text-fg-muted">
                  {r.line ?? "—"} · {r.turns} реплик
                </span>
              </button>
            ))
          )}
        </aside>
        <div className="flex min-w-0 max-w-3xl flex-col gap-2">
          {src === "chats" && chat ? (
            <ЧатПросмотр id={chat} />
          ) : src === "bot" && room ? (
            <БотПросмотр room={room} />
          ) : (
            <div className="text-[12.5px] text-fg-muted">Выберите разговор слева или введите номер чата</div>
          )}
        </div>
      </div>
      <Неузнанные open={(e) => go(e.chat ? { src: "chats", chat: e.chat } : { src: "bot", room: e.room ?? null })} />
    </div>
  );
}
